<script setup lang="ts">
import {
  BoxComponent,
  CampaignSelector,
  type ContentItemsFilter,
  DateRangeSelector,
  FlexComponent,
  ProfileSelector,
  type ProfileSelectorItem,
  type SocialItem,
  SocialItemEnum,
  SortingComponent,
} from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { useGetCampaigns } from '@/api/campaigns.api'
import ContentLibraryGrid from '@/components/ContentLibrary/ContentLibraryGrid.vue'
import { useSortingItems } from '@/lib/hooks'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useEntrypointStore } from '@/stores/entrypoint.stores'
import { useGlobalFilterStore } from '@/stores/global-filter.stores'
import CardComponent from '@/ui-library/atoms/CardComponent/CardComponent.vue'

const { sortingItems } = useSortingItems()
const props = defineProps<{
  influencer: { userId: number; username: string; socialMediaAccounts?: SocialItem[] }
  title?: string
}>()

const store = useStore()
const advertiserStore = useAdvertiserStore()
const entrypointStore = useEntrypointStore()
const advertiser = computed(() => advertiserStore?.advertiser)
const hideEdit = computed(() => store.getters['shop/hideEdit'])
const hasCampaigns = computed(() => entrypointStore.hasCampaigns)
const showCampaignSelector = computed(() => !hideEdit.value || hasCampaigns.value)

const filterStore = useGlobalFilterStore()
const page = ref(1)
const campaignSearchQuery = ref('')

const { data: campaigns, isLoading: isCampaignsLoading } = useGetCampaigns(advertiser, page, campaignSearchQuery)

const filter = ref<ContentItemsFilter>({
  page: 1,
  sortBy: 'date:desc',
  dateFrom: filterStore.dateRange?.startDate,
  dateTo: filterStore.dateRange?.endDate,
})

watch(
  () => props.influencer,
  (newInfluencer) => {
    if (newInfluencer?.userId) {
      filter.value = {
        ...filter.value,
        page: 1,
        profileRef: undefined,
        campaign: undefined,
        creator: { id: newInfluencer.userId, username: newInfluencer.username },
      }
    }
  },
  { immediate: true },
)

watch(
  () => filterStore.dateRange,
  (range) => {
    filter.value = { ...filter.value, page: 1, dateFrom: range?.startDate, dateTo: range?.endDate }
  },
  { deep: true },
)

const trafficSourceSocialTypes = new Set<SocialItemEnum>([SocialItemEnum.Instagram, SocialItemEnum.Snapchat, SocialItemEnum.TikTok])
const profileSelectorItems = computed<ProfileSelectorItem[]>(() =>
  (props.influencer.socialMediaAccounts ?? [])
    .filter((account) => trafficSourceSocialTypes.has(account.type))
    .map((account) => ({
      icon: account.icon,
      identifier: account.identifier,
      type: account.type,
      isPrimary: account.isPrimary,
      value: `${account.type}:${account.identifier}`,
      profileId: account.profileId,
    })),
)
</script>

<template>
  <CardComponent :title="props.title" header-align="center">
    <template #controls>
      <ProfileSelector v-model:profile-ref="filter.profileRef" v-model:profile-id="filter.profileId" :profiles="profileSelectorItems" />
      <FlexComponent justify="space-between" align="center" :gap="24">
        <CampaignSelector
          v-if="showCampaignSelector"
          v-model:campaign="filter.campaign"
          :campaigns="campaigns"
          :is-loading="isCampaignsLoading"
          :query="campaignSearchQuery"
          name-key="name"
          @update:query="campaignSearchQuery = $event"
        />
        <DateRangeSelector :date-range="filterStore.dateRange" @update:date-range="filterStore.setDateRange" />
        <SortingComponent v-model="filter.sortBy" :items="sortingItems" has-separate-order-sorting />
      </FlexComponent>
    </template>
    <BoxComponent :mb="8">
      <ContentLibraryGrid v-model="filter" :advertiser="advertiser" />
    </BoxComponent>
  </CardComponent>
</template>
<style scoped lang="scss">
:global(.date-range-selector__popper) {
  transform: translateX(-10px);
}
</style>
